// services/auth/OfflineManager.ts
// Управление offline режимом

import { consola } from 'consola'
import type { UserData, TokenData } from './authTypes'

// Logger
const logger = consola.withTag('offline-manager')

type OfflineEventHandler = (data?: unknown) => void

/**
 * Состояние offline
 */
export enum OfflineState {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  RECONNECTING = 'RECONNECTING',
}

/**
 * Отложенный запрос
 */
interface PendingRequest {
  id: string
  action: string
  payload: any
  timestamp: number
  retries: number
}

/**
 * OfflineManager - управление работой приложения offline
 * 
 * Решает проблемы:
 * - Потеря данных при offline
 * - Плохой UX при нестабильном интернете
 * - Внезапный logout при потере сети
 */
export class OfflineManager {
  private state: OfflineState = OfflineState.ONLINE
  private pendingRequests: PendingRequest[] = []
  private cachedUser: UserData | null = null
  private cachedTokens: TokenData | null = null
  private eventHandlers: Map<string, Set<OfflineEventHandler>> = new Map()
  
  // Конфигурация
  private readonly maxPendingRequests = 50
  private readonly maxRetries = 3
  private readonly syncInterval = 5000 // 5 секунд
  
  private syncTimer: NodeJS.Timeout | null = null
  
  constructor() {
    logger.info('Инициализация')
    
    /* v8 ignore next */
    if (process.client) {
      this.setupOnlineDetection()
      /* v8 ignore next */
      this.loadCachedData()
    }
  }
  
  /**
   * Настроить детекцию online/offline
   */
  private setupOnlineDetection(): void {
    window.addEventListener('online', () => {
      logger.info('Сеть доступна')
      this.handleOnline()
    })
    
    window.addEventListener('offline', () => {
      logger.info('Сеть недоступна')
      this.handleOffline()
    })
    
    // Проверяем начальное состояние
    /* v8 ignore next 3 */
    if (!navigator.onLine) {
      this.handleOffline()
    }
  }
  
  /**
   * Обработка перехода в online
   */
  private async handleOnline(): Promise<void> {
    this.state = OfflineState.RECONNECTING
    this.emit('stateChange', this.state)
    
    // Пытаемся синхронизировать отложенные запросы
    await this.syncPendingRequests()
    
    this.state = OfflineState.ONLINE
    this.emit('stateChange', this.state)
    this.emit('online')
    
    // Запускаем периодическую синхронизацию
    this.startSync()
  }
  
  /**
   * Обработка перехода в offline
   */
  private handleOffline(): void {
    this.state = OfflineState.OFFLINE
    this.emit('stateChange', this.state)
    this.emit('offline')
    
    // Останавливаем синхронизацию
    this.stopSync()
  }
  
  /**
   * Кешировать пользователя
   */
  /* v8 ignore start */
  public cacheUser(user: UserData): void {
    this.cachedUser = user
    /* v8 ignore next */
    if (process.client) {
      try {
        localStorage.setItem('offline_user', JSON.stringify(user))
        logger.success('Пользователь закеширован')
      } catch (error) {
        /* v8 ignore next 3 */
        logger.error('Ошибка кеширования пользователя', error)
      }
    }
  }
  
  /**
   * Кешировать токены
   */
  /* v8 ignore next 12 */
  public cacheTokens(tokens: TokenData): void {
    this.cachedTokens = tokens
    /* v8 ignore next */
    if (process.client) {
      try {
        localStorage.setItem('offline_tokens', JSON.stringify(tokens))
        logger.success('Токены закешированы')
      } catch (error) {
        /* v8 ignore next 3 */
        logger.error('Ошибка кеширования токенов', error)
      }
    }
  }
  /* v8 ignore stop */
  
  /**
   * Загрузить закешированные данные
   */
  private loadCachedData(): void {
    try {
      const userStr = localStorage.getItem('offline_user')
      /* v8 ignore next */
      if (userStr) {
        /* v8 ignore next 2 */
        this.cachedUser = JSON.parse(userStr)
      }
      
      const tokensStr = localStorage.getItem('offline_tokens')
      /* v8 ignore next 3 */
      if (tokensStr) {
        this.cachedTokens = JSON.parse(tokensStr)
      }
      
      /* v8 ignore next */
      logger.info('Данные загружены из кеша')
    /* v8 ignore next */
    } catch (error) {
      /* v8 ignore next 3 */
      logger.error('Ошибка загрузки кеша', error)
    }
  }
  
  /**
   * Получить закешированного пользователя
   */
  public getCachedUser(): UserData | null {
    return this.cachedUser
  }
  
  /**
   * Получить закешированные токены
   */
  public getCachedTokens(): TokenData | null {
    return this.cachedTokens
  }
  
  /**
   * Добавить запрос в очередь offline
   */
  public queueRequest(action: string, payload: any): void {
    if (this.pendingRequests.length >= this.maxPendingRequests) {
      logger.warn('Очередь offline запросов заполнена')
      // Удаляем самый старый запрос
      this.pendingRequests.shift()
    }
    
    const request: PendingRequest = {
      id: this.generateRequestId(),
      action,
      payload,
      timestamp: Date.now(),
      retries: 0,
    }
    
    this.pendingRequests.push(request)
    this.savePendingRequests()
    
    logger.info(`Запрос "${action}" добавлен в очередь offline`)
  }
  
  /**
   * Синхронизировать отложенные запросы
   */
  private async syncPendingRequests(): Promise<void> {
    if (this.pendingRequests.length === 0) {
      logger.info('Нет отложенных запросов для синхронизации')
      return
    }
    
    logger.info(`Синхронизация ${this.pendingRequests.length} отложенных запросов`)
    
    const requests = [...this.pendingRequests]
    this.pendingRequests = []
    
    for (const request of requests) {
      try {
        await this.executeRequest(request)
        logger.success(`Запрос "${request.action}" синхронизирован`)
      } catch (error) {
        logger.error(`Ошибка синхронизации "${request.action}"`, error)
        
        // Добавляем обратно если не превышен лимит попыток
        if (request.retries < this.maxRetries) {
          request.retries++
          this.pendingRequests.push(request)
        }
      }
    }
    
    this.savePendingRequests()
    this.emit('synced', { total: requests.length, failed: this.pendingRequests.length })
  }
  
  /**
   * Выполнить запрос
   */
  private async executeRequest(request: PendingRequest): Promise<void> {
    // Эмитим событие для обработки запроса
    const handlers = this.eventHandlers.get(`execute:${request.action}`)
    if (handlers && handlers.size > 0) {
      for (const handler of handlers) {
        await handler(request.payload)
      }
    } else {
      logger.warn(`Нет обработчика для "${request.action}"`)
    }
  }
  
  /**
   * Сохранить отложенные запросы
   */
  private savePendingRequests(): void {
    /* v8 ignore next */
    if (process.client) {
      try {
        localStorage.setItem('offline_pending_requests', JSON.stringify(this.pendingRequests))
      /* v8 ignore next */
      } catch (error) {
        /* v8 ignore next 3 */
        logger.error('Ошибка сохранения отложенных запросов', error)
      }
    }
  }
  
  /**
   * Загрузить отложенные запросы
   */
  private loadPendingRequests(): void {
    if (process.client) {
      try {
        const requestsStr = localStorage.getItem('offline_pending_requests')
        /* v8 ignore next 4 */
        if (requestsStr) {
          this.pendingRequests = JSON.parse(requestsStr)
          logger.info(`Загружено ${this.pendingRequests.length} отложенных запросов`)
        }
      /* v8 ignore next */
      /* v8 ignore next */
      } catch (error) {
        /* v8 ignore next 2 */
        logger.error('Ошибка загрузки отложенных запросов', error)
      }
    }
  }
  
  /**
   * Запустить периодическую синхронизацию
   */
  private startSync(): void {
    if (this.syncTimer) return
    
    this.syncTimer = setInterval(async () => {
      if (this.state === OfflineState.ONLINE && navigator.onLine) {
        await this.syncPendingRequests()
      }
    }, this.syncInterval)
  }
  
  /**
   * Остановить синхронизацию
   */
  private stopSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
  }
  
  /**
   * Получить текущее состояние
   */
  public getState(): OfflineState {
    return this.state
  }
  
  /**
   * Проверить, online ли
   */
  public isOnline(): boolean {
    return this.state === OfflineState.ONLINE && navigator.onLine
  }
  
  /**
   * Проверить, offline ли
   */
  public isOffline(): boolean {
    return this.state === OfflineState.OFFLINE || !navigator.onLine
  }
  
  /**
   * Очистить кеш
   */
  public clearCache(): void {
    this.cachedUser = null
    this.cachedTokens = null
    this.pendingRequests = []
    
    if (process.client) {
      localStorage.removeItem('offline_user')
      localStorage.removeItem('offline_tokens')
      localStorage.removeItem('offline_pending_requests')
    }
    
    logger.info('Кеш очищен')
  }
  
  /**
   * Подписаться на события
   */
  public on(event: string, handler: OfflineEventHandler): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    
    this.eventHandlers.get(event)!.add(handler)
    
    return () => {
      this.eventHandlers.get(event)?.delete(handler)
    }
  }
  
  /**
   * Эмитировать событие
   * Публичный метод для возможности эмита событий извне (например, из API)
   */
  public emit(event: string, data?: any): void {
    const handlers = this.eventHandlers.get(event)
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(data)
        } catch (error) {
          logger.error(`Ошибка в обработчике события ${event}`, error)
        }
      }
    }
  }
  
  /**
   * Генерировать ID запроса
   */
  private generateRequestId(): string {
    return `offline_${Date.now()}_${Math.random().toString(36).slice(7)}`
  }
  
  /**
   * Получить статистику
   */
  public getStats(): {
    state: OfflineState
    pendingRequests: number
    hasCachedUser: boolean
    hasCachedTokens: boolean
  } {
    return {
      state: this.state,
      pendingRequests: this.pendingRequests.length,
      hasCachedUser: !!this.cachedUser,
      hasCachedTokens: !!this.cachedTokens,
    }
  }
}

// Экспортируем единственный экземпляр
export const offlineManager = new OfflineManager()

